import {useMutation, useQueries, useQuery, useSuspenseQuery} from '@tanstack/react-query';
import type {SearchResultMetadata} from 'analytics/globalSearchAnalytics';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {
  EditorDoc,
  EditordocService,
  Employee,
  EmployeeLdap,
  EmployeeService,
  EmployeeWithSearchMetadata,
  KnowledgeSearchResult,
  NewsPost,
  NewsService,
  PeopleSearchRankingMode,
  SearchService,
  Team,
  TeamService,
  TeamWithCounts,
  UpdateEmployeeTeam,
  WorkstreamBasic,
  WorkstreamSearchNavigationItemWithSearchMetadata,
} from 'client';
import {Filters} from 'components/search/GlobalSearch';
import {rankSearchWorkstreams} from 'hooks/searchWorkstreamFallback';
import {useAtomValue} from 'jotai';
import {useRef} from 'react';
import {useSearchParams} from 'react-router';
import {getEmployeeService, getFrontendUrl, getService} from 'utilities';
import {queryClient} from 'views/QueryClientWrapper';
import {useWorkstream} from 'views/workstreams/hooks';
import {useStoredRecents} from 'views/workstreams/page/recentsStorage';
import {pickRecentItems, useRecentWorkstreamRows} from 'views/workstreams/page/recentsStripData';

interface CustomItem {
  route: string;
}

type WorkstreamSearchNavigationItem = Omit<
  WorkstreamSearchNavigationItemWithSearchMetadata,
  'search_metadata'
>;

type SearchableResult<T> = T & {
  // Fallback results and responses from a backend that has not rolled out BE-1
  // do not have metadata yet. Analytics supplies bounded fallback values there.
  search_metadata?: SearchResultMetadata;
};

export type SearchEmployee = EmployeeWithSearchMetadata;

// To test a new ranking policy, add it to the backend API first, then gate the
// client-side mode selection with GrowthBook and record its variant in Amplitude.
export const PEOPLE_SEARCH_RANKING_MODE = PeopleSearchRankingMode.PERSONALIZED_V1;

const searchPeople = (
  value: string,
  rankingMode: PeopleSearchRankingMode
): Promise<SearchEmployee[]> =>
  getEmployeeService().searchPeopleApiV1PeopleSearchGet(value, undefined, rankingMode);

export const getPeopleSearchQueryKey = (
  input: string | null,
  rankingMode: PeopleSearchRankingMode
) => ['search', 'people', input, rankingMode] as const;

export const getPeopleMultiSearchQueryKey = (
  ldap: string | undefined,
  rankingMode: PeopleSearchRankingMode
) => ['employee', 'all', ldap, rankingMode] as const;

export const useEmployee = ({
  ldap,
  throwOnError = true,
}: {
  ldap?: string;
  throwOnError?: boolean;
}) => {
  const {data, isLoading} = useQuery({
    queryKey: ['employee', ldap],
    queryFn: () => getService(EmployeeService).getEmployeeByLdapApiV1PeopleLdapLdapGet(ldap ?? ''),
    throwOnError,
    enabled: !!ldap,
  });

  return {employee: data, isLoading};
};

export const useEmployees = ({ldaps}: {ldaps: string[]}) => {
  const dedupedLdaps = Array.from(new Set(ldaps));

  const employees = useQueries({
    queries: dedupedLdaps.filter(Boolean).map((ldap) => ({
      queryKey: ['employee', ldap],
      queryFn: () => getEmployeeService().getEmployeeByLdapApiV1PeopleLdapLdapGet(ldap),
      throwOnError: false,
      suspense: true,
    })),
  })
    .map(({data}) => {
      // Populate employee query keys to save an extra request
      if (data) {
        queryClient.setQueryData(['employee', data.ldap], data);
      }

      return data;
    })
    .filter(Boolean);

  return employees as Employee[];
};

export const useEmployeesBatch = ({ldaps, enabled}: {ldaps: string[]; enabled: boolean}) => {
  const dedupedLdaps = Array.from(new Set(ldaps));
  const {data: employees = [], isLoading} = useQuery({
    queryKey: ['employees', dedupedLdaps],
    queryFn: () => getEmployeeService().getEmployeesByLdapsApiV1PeopleLdapsPost(dedupedLdaps),
    enabled,
  });

  return {employees, isLoading};
};

export const useEmployeesBatchSuspense = ({
  ldaps,
  enabled,
}: {
  ldaps: string[];
  enabled: boolean;
}) => {
  const dedupedLdaps = Array.from(new Set(ldaps));
  const {data: employees = []} = useSuspenseQuery({
    queryKey: ['employees', dedupedLdaps],
    queryFn: enabled
      ? () => getEmployeeService().getEmployeesByLdapsApiV1PeopleLdapsPost(dedupedLdaps)
      : () => Promise.resolve([]),
  });

  return employees as Employee[];
};

export const useEditTeams = ({id, ldap}: {id: string; ldap: string}) => {
  const {mutateAsync: editTeams, isPending} = useMutation({
    mutationFn: ({teams}: {teams: (UpdateEmployeeTeam & {slug: string})[]}) =>
      getService(EmployeeService).editTeamsApiV1PeopleLdapTeamsPut(ldap, teams),
    onSettled: (_data, _error, {teams}) => {
      queryClient.invalidateQueries({queryKey: ['checkup', id]});
      queryClient.invalidateQueries({queryKey: ['profile', id]});
      teams.map((team) => queryClient.invalidateQueries({queryKey: ['checkup', team.slug]}));
    },
  });

  return {editTeams, isPending};
};

export const useSearchesWithStatus = ({
  ldaps,
  rankingMode,
}: {
  ldaps?: string[];
  rankingMode?: PeopleSearchRankingMode;
}) => {
  const effectiveRankingMode = rankingMode ?? PEOPLE_SEARCH_RANKING_MODE;
  const dedupedLdaps = Array.from(new Set(ldaps));

  const employees =
    useQueries({
      queries: dedupedLdaps.map((ldap) => ({
        enabled: Boolean(ldap),
        queryKey: getPeopleMultiSearchQueryKey(ldap, effectiveRankingMode),
        queryFn: ldaps ? () => searchPeople(ldap ?? '', effectiveRankingMode) : undefined,
        throwOnError: false,
      })),
    }) ?? [];

  return {
    results: employees.flatMap(({data}) => data).filter(Boolean) as SearchEmployee[],
    isLoading: employees.some(({isLoading}) => isLoading),
  };
};

export const useSearches = ({
  ldaps,
  rankingMode,
}: {
  ldaps?: string[];
  rankingMode?: PeopleSearchRankingMode;
}) => useSearchesWithStatus({ldaps, rankingMode}).results;

// Search could populate `employee, ldap` query ^
export const useSearchWithStatus = ({
  input,
  rankingMode,
}: {
  input: string | null;
  rankingMode?: PeopleSearchRankingMode;
}) => {
  const effectiveRankingMode = rankingMode ?? PEOPLE_SEARCH_RANKING_MODE;
  const cached = useRef<SearchEmployee[]>([]);
  const {data, isLoading} = useQuery({
    queryKey: getPeopleSearchQueryKey(input, effectiveRankingMode),
    queryFn: () => searchPeople(input ?? '', effectiveRankingMode),
    enabled: !!input,
  });

  if (!input?.length) {
    return {results: [], isLoading: false};
  }

  if (isLoading) {
    return {results: cached.current, isLoading: true};
  }

  cached.current = data ?? [];

  return {results: cached.current, isLoading: false};
};

export const useSearch = ({
  input,
  rankingMode,
}: {
  input: string | null;
  rankingMode?: PeopleSearchRankingMode;
}) => useSearchWithStatus({input, rankingMode}).results;

export type GlobalSearchResults =
  | SearchableResult<Employee>
  | SearchableResult<EmployeeLdap>
  | SearchableResult<TeamWithCounts>
  | SearchableResult<WorkstreamBasic>
  | SearchableResult<NewsPost>
  | SearchableResult<CustomItem>
  | SearchableResult<EditorDoc>
  | SearchableResult<WorkstreamSearchNavigationItem>
  | KnowledgeSearchResult
  | {idx: number}
  | undefined;

export const isEmployee = (
  selected: GlobalSearchResults
): selected is SearchableResult<Employee> => {
  return (selected as EmployeeLdap)?.user_id !== undefined;
};

export const isTeam = <T>(selected: T): selected is Extract<T, Team> => {
  return (selected as Team)?.team_id !== undefined;
};

export const isTeamSearchResult = (
  selected: GlobalSearchResults
): selected is SearchableResult<TeamWithCounts> => isTeam(selected);

export const isWorkstream = (
  selected: GlobalSearchResults
): selected is SearchableResult<WorkstreamBasic> => {
  return (selected as WorkstreamBasic)?.employee_associations !== undefined;
};

export const isWorkstreamNavigation = (
  selected: GlobalSearchResults
): selected is WorkstreamSearchNavigationItem => {
  return (selected as WorkstreamSearchNavigationItem)?.search_item_type === 'workstream_navigation';
};

export const isKnowledgeResult = (
  selected: GlobalSearchResults
): selected is KnowledgeSearchResult => {
  return (selected as KnowledgeSearchResult)?.search_item_type === 'knowledge';
};

export const isNewsPost = (selected: GlobalSearchResults): selected is NewsPost => {
  return (selected as NewsPost)?.category !== undefined;
};

export const isPlanning = (selected: GlobalSearchResults): selected is EditorDoc => {
  return (selected as EditorDoc)?.template_type !== undefined;
};

export const isCustom = (selected: GlobalSearchResults): selected is CustomItem => {
  return (selected as CustomItem)?.route !== undefined;
};

const useSearchFallbacks = (enabled: boolean, shortList: boolean) => {
  const {employee, teamMembers, reportingLine, directReports} = useAtomValue(loggedInEmployeeAtom);

  const [myProfile, manager, ...orgLine] = reportingLine;
  const members = directReports.length > 0 ? directReports : teamMembers;

  const {data: teams} = useQuery({
    queryKey: ['myTeamsPath'],
    queryFn: () => getService(TeamService).getHierarchyForTeamApiV1TeamsHierarchyGet(),
    enabled,
  });

  const {data: news} = useQuery({
    queryKey: ['news'],
    queryFn: () => getService(NewsService).getPostsApiV1NewsGet(),
    enabled,
  });

  const {workstream: tracker} = useWorkstream(enabled ? employee?.ldap : undefined);
  const [storedRecents] = useStoredRecents();
  const recents = enabled ? pickRecentItems(storedRecents) : [];
  const {data: recentRows} = useRecentWorkstreamRows(
    recents.flatMap((item) => (item.type === 'workstream' ? [item.id] : []))
  );
  const workstreams = rankSearchWorkstreams({
    recents,
    mine: tracker?.dependencies.map(({dependent}) => dependent) ?? [],
    recentRows,
    myLdap: employee?.ldap,
  });

  const {data: planning} = useQuery({
    queryKey: ['planning'],
    queryFn: () => getService(EditordocService).getNarrativesApiV1EditordocNarrativesGet(),
    enabled,
  });

  return {
    all: shortList
      ? // Global search's short Suggested list (Ask entry redesign): your manager,
        // two teammates, your team and your latest workstream. Your own profile
        // is in the avatar menu.
        [manager, ...members.slice(0, 2), teams?.[0], ...workstreams.slice(0, 1)]
      : [
          {
            ...myProfile,
            name: 'Your profile',
            role: `${getFrontendUrl()}people/${myProfile?.ldap}`,
          },
          manager,
          ...members.slice(0, 4),
          teams?.[0],
          ...workstreams,
        ],
    people: [manager, ...members, ...orgLine, myProfile],
    teams: teams ?? [],
    workstreams,
    news: news ?? [],
    planning: planning ?? [],
  };
};

// Search could populate `employee, ldap` query ^

export const INITIAL_RESULTS = 8;
export const MAX_RESULTS = 50; // Could be 50 after DSR-515

export const getGlobalSearchQueryKey = ({
  input,
  filter,
  showMore,
  limit,
  includeWorkstreamScopes,
  rankingMode,
}: {
  input: string | null;
  filter: Filters;
  showMore?: boolean;
  limit?: number;
  includeWorkstreamScopes: boolean;
  rankingMode: PeopleSearchRankingMode;
}) => ['search', input, filter, showMore, limit, includeWorkstreamScopes, rankingMode] as const;

export const useGlobalSearch = ({
  input,
  filter,
  showMore,
  limit,
  preloadFallback = true,
  shortFallbacks = false,
  includeWorkstreamScopes = false,
  rankingMode,
}: {
  input: string | null;
  filter: Filters;
  showMore?: boolean;
  limit?: number;
  preloadFallback?: boolean;
  /** Empty-input suggestions as global search's short Suggested list. */
  shortFallbacks?: boolean;
  includeWorkstreamScopes?: boolean;
  rankingMode?: PeopleSearchRankingMode;
}) => {
  const effectiveRankingMode = rankingMode ?? PEOPLE_SEARCH_RANKING_MODE;
  const [searchParams] = useSearchParams();

  const searchFallbacks = useSearchFallbacks(preloadFallback, shortFallbacks);
  const {data, isLoading, isFetching} = useQuery({
    queryKey: getGlobalSearchQueryKey({
      input,
      filter,
      showMore,
      limit,
      includeWorkstreamScopes,
      rankingMode: effectiveRankingMode,
    }),
    queryFn: () =>
      getService(SearchService).searchApiV1SearchGet(
        input ?? '',
        filter,
        Boolean(searchParams.get('s')),
        includeWorkstreamScopes,
        limit ? limit : showMore ? MAX_RESULTS : INITIAL_RESULTS + 1,
        undefined,
        effectiveRankingMode
      ),
    enabled: !!input,
    placeholderData: (previousData) => previousData,
  });

  if (!input?.length) {
    return {data: searchFallbacks[filter] ?? [], isLoading, isFetching};
  }

  return {data: (data ?? []) as GlobalSearchResults[], isLoading, isFetching};
};
